import { useCallback, useContext, useState } from 'react';
import { RootStoreContext } from '../global/storeContext';

/**
 * Gates a sensitive action behind profile completion + identity verification.
 *
 * Usage:
 *   const { checkAndProceed, gateDialogProps } = useIdentityGate();
 *   const handleSubmit = () => checkAndProceed(async () => { ...real handler... });
 *   // in JSX:
 *   <CompleteAccountSetupDialog {...gateDialogProps} />
 *
 * Policy: the user must have both USER_PROFILE_COMPLETE and IDENTITY_VERIFIED to proceed.
 * If either is missing, the action is skipped and the gate dialog opens, directing the
 * user to /account/personal-info where they can complete both.
 */
export const useIdentityGate = () => {
  const { userStore } = useContext(RootStoreContext);
  const [dialogOpen, setDialogOpen] = useState(false);

  const checkAndProceed = useCallback(
    async (action: () => unknown | Promise<unknown>): Promise<void> => {
      const steps = await userStore.getAccountSetupSteps();
      if (!steps?.IDENTITY_VERIFIED || !steps.USER_PROFILE_COMPLETE) {
        setDialogOpen(true);
        return;
      }
      await action();
    },
    [userStore],
  );

  const gateDialogProps = {
    openCompleteProfileDialog: dialogOpen,
    handleCloseDialog: () => setDialogOpen(false),
  };

  return { checkAndProceed, gateDialogProps };
};
